/**
 * Background music: one streamed, looping track per scene on the mixer's music bus ("Music &
 * fanfares" volume). Tracks stream through an <audio> element (a 3-minute song is never decoded
 * into memory) routed via a MediaElementSource and a fade gain, so the music volume slider, tab
 * suspend and the sound-off switch all apply as for every other bus.
 *
 * Scenes call `music.play(id)` / `music.play(null)`; changes crossfade (old out, new in). Nothing
 * starts before the mixer's context exists (first user gesture), and every failure (unsupported
 * format, blocked autoplay, missing file) leaves the game silent rather than throwing.
 */
import { mixer } from './mixer';

export type MusicId = 'mossvale';

/** Track files (public/assets/audio/music): Opus/WebM first, MP3 fallback. */
export const MUSIC_TRACKS: Readonly<Record<MusicId, { webm: string; mp3: string; gain: number }>> = {
  // "bg-music-1" (supplied by the project owner), normalised to -20 LUFS for a bed under the SFX.
  mossvale: { webm: 'assets/audio/music/mossvale-1.webm', mp3: 'assets/audio/music/mossvale-1.mp3', gain: 0.85 },
};

const FADE_IN_S = 2.5;
const FADE_OUT_S = 1.8;

interface Voice {
  id: MusicId;
  el: HTMLAudioElement;
  src: MediaElementAudioSourceNode;
  fade: GainNode;
}

/** The track file a browser should load (pure; `canPlay` is HTMLMediaElement.canPlayType). */
export function pickMusicSrc(id: MusicId, canPlay: (type: string) => string): string {
  const t = MUSIC_TRACKS[id];
  return canPlay('audio/webm; codecs="opus"') ? t.webm : t.mp3;
}

class MusicPlayer {
  private want: MusicId | null = null;
  private voice: Voice | null = null;
  private waiting = false;

  /** Play `id` (crossfading from whatever plays) or fade to silence with null. */
  play(id: MusicId | null): void {
    this.want = id;
    if (typeof document === 'undefined') return;
    if (!mixer.ctx) {
      if (!this.waiting) {
        this.waiting = true;
        mixer.onReady(() => {
          this.waiting = false;
          this.sync();
        });
      }
      return;
    }
    this.sync();
  }

  private sync(): void {
    const ctx = mixer.ctx;
    if (!ctx) return;
    if (this.voice?.id === this.want) return;
    if (this.voice) this.stop(this.voice, ctx);
    this.voice = null;
    if (!this.want) return;
    const out = mixer.input('music');
    if (!out) return;
    try {
      const el = new Audio();
      el.loop = true;
      el.preload = 'auto';
      el.crossOrigin = 'anonymous';
      el.src = pickMusicSrc(this.want, (t) => el.canPlayType(t));
      const src = ctx.createMediaElementSource(el);
      const fade = ctx.createGain();
      fade.gain.value = 0;
      src.connect(fade).connect(out);
      const t = ctx.currentTime;
      fade.gain.setValueAtTime(0, t);
      fade.gain.linearRampToValueAtTime(MUSIC_TRACKS[this.want].gain, t + FADE_IN_S);
      void el.play().catch(() => undefined);
      this.voice = { id: this.want, el, src, fade };
    } catch {
      this.voice = null;
    }
  }

  private stop(v: Voice, ctx: AudioContext): void {
    const t = ctx.currentTime;
    try {
      v.fade.gain.cancelScheduledValues(t);
      v.fade.gain.setValueAtTime(v.fade.gain.value, t);
      v.fade.gain.linearRampToValueAtTime(0, t + FADE_OUT_S);
    } catch {
      /* already gone */
    }
    setTimeout(() => {
      try {
        v.el.pause();
        v.el.removeAttribute('src');
        v.el.load();
        v.src.disconnect();
        v.fade.disconnect();
      } catch {
        /* ignore */
      }
    }, FADE_OUT_S * 1000 + 100);
  }
}

export const music = new MusicPlayer();
